import { useQuery, useQueryClient } from '@tanstack/react-query';
import { AnimatePresence, motion } from 'framer-motion';
import {
	Fragment,
	lazy,
	Suspense,
	useCallback,
	useEffect,
	useLayoutEffect,
	useMemo,
	useRef,
	useState,
	type KeyboardEvent,
	type PointerEvent,
	type RefObject,
} from 'react';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink, useNavigate } from 'react-router-dom';

import { AnalyticsEvent, useAnalytics } from '@bonusly/analytics';
import {
	AlertDialog,
	AlertDialogBody,
	AlertDialogCloseButton,
	AlertDialogContent,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogOverlay,
	Badge,
	Box,
	Button,
	CheveronSmallLeftOutline,
	CogOutline,
	Flex,
	HStack,
	Link,
	MaximizeOutline,
	MenuAlt2Outline,
	MinimizeOutline,
	ShareOutline,
	Spinner,
	Text,
	useToast,
	XOutline,
} from '@bonusly/design-system';
import { Permission, useCurrentCompany, useCurrentUser, useFeatureFlag, useIsBonuslyCompany, usePermission } from '@bonusly/who-am-i';

import { useScrollAnchor } from '../../lib/bizy/connector';
import type { BizyUserConversationDetail, BizyUserMessageNode } from '../../lib/bizy/types';
import { useBizyActivationState, useBizyFeedbackMutation, useBizyTitleMutation } from '../../queries/bizy';
import {
	bizyUserConversationQueryKey,
	bizyUserConversationsQueryKey,
	useBizyShareConversationMutation,
	useBizyUpdateConversationMutation,
	useBizyUserConversations,
} from '../../queries/bizy-conversations';
import { Bizy, useBizyBodyGiggle, useBizyFlightAnchor, useIsBizyFlightAnchorHidden, type BizyState } from '../bizy';
import { BizyAmbientGlow } from './bizy-ambient-glow';
import { BizyHeaderIconButton, BizySidePanelHeader } from './bizy-side-panel-header';
import { BizySidePanelSuggestedPrompts } from './bizy-side-panel-suggested-prompts';

import './lib/bizy-whats-new-tip-dev-helpers';

import { BizyComposer, type BizyComposerHandle } from './components/bizy-composer';
import { BizyFeedbackModal } from './components/bizy-feedback-modal';
import { BizyFreshStartGreeting } from './components/bizy-fresh-start-greeting';
import { BizyGameInvite } from './components/bizy-game-invite';
import { BizyGreeterCallout } from './components/bizy-greeter-callout';
import { BizyInputBanner } from './components/bizy-input-banner';
import { BIZY_ALLOWED_MODELS, loadStoredBizyModel, saveStoredBizyModel } from './components/bizy-model-selector';
import { BizyTimeDivider } from './components/bizy-time-divider';
import { BizyTimelineMessage, type BizyTimelineMessageProps } from './components/bizy-timeline-message';
import { useBizyChatScrollController } from './hooks/use-bizy-chat-scroll-controller';
import { useBizyConnectorCounts } from './hooks/use-bizy-connector-counts';
import { useBizyExternalOpenAutoSend } from './hooks/use-bizy-external-open-auto-send';
import { useBizyFamiliarGreeting } from './hooks/use-bizy-familiar-greeting';
import { useBizyGameEasterEgg } from './hooks/use-bizy-game-easter-egg';
import { useBizyGreeterCallout, useBizyGreeting } from './hooks/use-bizy-greeting';
import { bizyGreetingFallbackName, useBizyGreetingDisplayName } from './hooks/use-bizy-greeting-display-name';
import { useBizyIdleBlankSlate } from './hooks/use-bizy-idle-blank-slate';
import { useBizyPeopleEntries } from './hooks/use-bizy-people-entries';
import { useBizyPromptLibrary } from './hooks/use-bizy-prompt-library';
import { useBizySidePanelChat } from './hooks/use-bizy-side-panel-chat';
import { useBizyVoiceInput } from './hooks/use-bizy-voice-input';
import { buildPersonalizedGenerationKey, pickRandomPersonalizedChipPrompt, useBizyWhatsNewTip } from './hooks/use-bizy-whats-new-tip';
import { useLoadProactiveConversationOnOpen } from './hooks/use-load-proactive-conversation-on-open';
import { usePeopleBrowserEnabled } from './hooks/use-people-browser-enabled';
import { getMessageDividers, newestMessageTimestamp } from './lib/bizy-message-timestamps';
import { getActiveConversationTitle, getBizyConversationTitle } from './lib/conversation-title';
import { getCachedTitle, setCachedTitle } from './lib/conversation-title-cache';
import { getToolProgressKey } from './lib/notes-edit-detection';
import { partnerNameFromPageName } from './lib/prompt-library-partner-name';
import { getAudienceSegment, getPromptChips, isTrialSegment, type ChipContext } from './lib/suggested-prompts';
import { useBuildBizyUiBlockActionDispatcher } from './lib/ui-block-action-dispatcher';
import { useBizyKitRendererProps } from './lib/use-bizy-kit-renderer-props';
import { useBizyActiveComposersStore } from './store/use-bizy-active-composers-store';
import { useBizyContextualInsertStore } from './store/use-bizy-contextual-insert-store';
import { useBizyExternalOpenStore } from './store/use-bizy-external-open-store';
import { useBizyPageContextStore } from './store/use-bizy-page-context-store';
import { useBizyPendingChatMessageStore } from './store/use-bizy-pending-chat-message-store';
import { useBizySidePanelStore } from './store/use-bizy-side-panel-store';

const RECENT_CONVERSATIONS_LIMIT = 5;
const LazyBizySwarmGame = lazy(() => import('./components/bizy-swarm-game').then((module) => ({ default: module.BizySwarmGame })));

// Blank-slate scroll geometry (BIZY-683): the divider lands this far below the
// container top, keeping the tail of the previous exchange visible above it —
// roughly the last two lines of the prior reply plus its feedback toolbar (per
// design mock). When the content above the divider is shorter than this, the
// scroll simply clamps and the divider sits lower; no special-casing.
const BLANK_SLATE_SCROLL_OFFSET = 120;
// Pre-paint estimate of the divider row's rendered height (vertical padding +
// label). Used only for the region's initial min-height; replaced by the real
// `offsetHeight` measurement once the divider is in the DOM.
const BLANK_SLATE_DIVIDER_ALLOWANCE = 60;
// The timeline column's inter-item gap (the inner <Flex gap={2}>).
const TIMELINE_ROW_GAP_PX = 8;
// Subtracted from the container height when sizing the blank-slate region:
// everything but the sliver above the divider and the divider itself, so the
// greeting cluster fills the viewport below the divider without leaving dead
// scroll space beneath. Derived so the two offsets can't drift apart.
const BLANK_SLATE_VIEWPORT_INSET = BLANK_SLATE_SCROLL_OFFSET + BLANK_SLATE_DIVIDER_ALLOWANCE;

interface PromptChipEntry {
	key: string;
	prompt: string;
	categoryId?: 'personalized';
}

interface BizySidePanelChatViewProps {
	onConversationIdChange?: (conversationId: string | undefined) => void;
	onOpenHistory: () => void;
	showFullscreenToggle?: boolean;
}

export function BizySidePanelChatView({ onConversationIdChange, onOpenHistory, showFullscreenToggle = true }: BizySidePanelChatViewProps) {
	const { t } = useTranslation('admin_bizy');
	const { track } = useAnalytics();
	const toast = useToast();
	const { mutate: submitFeedback } = useBizyFeedbackMutation();
	const { mutate: updateConversation } = useBizyUpdateConversationMutation();
	const { mutate: shareConversation } = useBizyShareConversationMutation();
	const { mutate: generateTitle } = useBizyTitleMutation();
	const pageContext = useBizyPageContextStore((state) => state.context);
	const pageType = pageContext.pageType;
	const user = useCurrentUser();
	const isBizyModelExperimentEnabled = useFeatureFlag('bizy-model-experiment');
	const [selectedBizyModel, setSelectedBizyModel] = useState(() => loadStoredBizyModel(user.id));
	useEffect(() => {
		if (!isBizyModelExperimentEnabled) return;
		setSelectedBizyModel(loadStoredBizyModel(user.id));
	}, [isBizyModelExperimentEnabled, user.id]);
	const handleSelectBizyModel = useCallback(
		(model: string) => {
			if (!BIZY_ALLOWED_MODELS.has(model)) return;
			setSelectedBizyModel(model);
			saveStoredBizyModel(user.id, model);
		},
		[user.id]
	);

	const handleSuggestedPageClick = useCallback(
		(path: string) => {
			track(AnalyticsEvent.Bizy.SuggestedPageClicked, {
				pagePath: path,
				currentPath: window.location.pathname,
			});
		},
		[track]
	);
	const {
		messages,
		conversationId,
		isLoading,
		isTimedOut,
		isOtherTabLoading,
		isLoadingConversation,
		bizyEmotion,
		activeToolCalls,
		sendMessage,
		sendMessageInNewConversation,
		cancelGeneration,
		resetChat,
		retryLastMessage,
		updateMessageFeedback,
		updateNodeChoice,
		loadConversation,
		archivedSegments,
		isContinuous,
		didInitLoad,
		isNewSegment,
		hasMoreHistory,
		isLoadingOlder,
		loadOlderTurns,
	} = useBizySidePanelChat({ model: isBizyModelExperimentEnabled ? selectedBizyModel : undefined });
	// Defined early so it's available when useBizyPromptLibrary is called below.
	const inputDisabled = isLoading || isOtherTabLoading;
	const queryClient = useQueryClient();
	const panelView = useBizySidePanelStore((state) => state.view);

	const composerRef = useRef<BizyComposerHandle>(null);
	// Stable wrapper so hooks/effects that capture setComposerText in a closure
	// always call through to the latest composerRef without triggering re-renders.
	const setComposerText = useCallback((text: string) => composerRef.current?.setInputText(text), []);

	const [isFeedbackModalOpen, setIsFeedbackModalOpen] = useState(false);
	const [feedbackMessageId, setFeedbackMessageId] = useState<string | null>(null);
	const [titleOverride, setTitleOverride] = useState<string | undefined>();
	const { isGiggling: isBizyTickled, triggerGiggle: triggerBizyGiggle } = useBizyBodyGiggle();
	const {
		isPromptOpen: isBizyGamePromptOpen,
		isGameOpen: isBizyGameOpen,
		holdProgress: bizyGameHoldProgress,
		isHolding: isBizyGameHolding,
		startBizyHold,
		cancelBizyHold,
		dismissPrompt: dismissBizyGamePrompt,
		openGame: openBizyGame,
		closeGame: closeBizyGame,
	} = useBizyGameEasterEgg();
	const { containerRef: scrollAnchorRef, beforePrepend } = useScrollAnchor<HTMLDivElement>();
	const lastUserMsgRef = useRef<HTMLDivElement>(null);
	const hasTrackedBizyGameInviteShownRef = useRef(false);

	// ── Idle blank slate (BIZY-683) ───────────────────────────────────
	// When the conversation is stale (no activity for 1 hour, evaluated on
	// panel open / load / window focus — see useBizyIdleBlankSlate), the
	// timeline scrolls up to a fresh-start viewport (divider + empty-state
	// greeting) without wiping the rendered messages. Sending from that
	// viewport archives the visible conversation (classic) or simply continues
	// it (continuous — the server owns segmentation).
	//
	// `blankSlate` holds the divider's boundary time and the measured
	// min-height of the fresh-start region; null when not showing. Archived
	// segments live in the adapter, which clears them on every timeline
	// replacement itself.
	const [blankSlate, setBlankSlate] = useState<{ boundaryAt: Date; minHeight: number } | null>(null);
	const isBlankSlate = blankSlate !== null;
	const blankSlateDividerRef = useRef<HTMLDivElement>(null);
	// Scroll mechanics (smooth animation, bottom jumps) live in the controller;
	// this component makes intent-level calls.
	const scroll = useBizyChatScrollController({ isBlankSlate });
	// A composer draft defers the blank slate — never yank the viewport
	// mid-composition. Fed by the composer's onDraftStateChange, which fires on
	// every empty ⇆ non-empty transition regardless of source (typing, voice
	// transcript, cancel restore, prefills, submit/reset clears).
	const [isComposerDrafting, setIsComposerDrafting] = useState(false);

	// Leave the blank slate. Cancels an in-flight positioning animation — its
	// target getter reads the divider's offsetTop, which is about to unmount.
	//
	// `preserveScrollRoom` is set when a send is what's leaving the blank slate.
	// enterBlankSlate zeroed the send-scroll bottom padding (the spacer region
	// supplied the room instead); that spacer is about to unmount. Without the
	// padding the scroll height collapses for one frame, so the browser clamps
	// scrollTop to the new bottom — a downward jump landing the new turn on the
	// composer — before the post-commit send-scroll effect re-adds the padding
	// and animates the turn back up. Restoring the padding synchronously here,
	// before setBlankSlate(null) commits the unmount, keeps the scroll height
	// stable across the transition, leaving only the single upward scroll to the
	// new turn. The other callers (New chat, panel close, empty timeline) reset
	// the conversation rather than send, so they leave the padding at zero.
	const dismissBlankSlate = useCallback(
		(options?: { preserveScrollRoom?: boolean }) => {
			scroll.cancelSmoothScroll();
			if (options?.preserveScrollRoom) {
				const container = scroll.containerRef.current;
				const inner = container?.firstElementChild as HTMLElement | null;
				if (container && inner) inner.style.paddingBottom = `${container.clientHeight}px`;
			}
			setBlankSlate(null);
		},
		[scroll]
	);

	// Send from a fresh-start surface (composer, starter chips, prompt library,
	// What's New tip, contextual `/` starter). From the blank slate in classic
	// mode this opens a new backend conversation (the adapter archives the
	// visible one above its divider); otherwise it's a plain send that
	// continues the conversation. Either way the view scrolls to the new turn.
	const sendFromFreshStartSurface = useCallback(
		(content: string, extraContext?: Record<string, string>, sendOptions?: { isEntryContextStarter?: boolean }) => {
			// A fresh start in either mode must not inherit the prior thread's
			// entry-point context (contextual `/` fields, feature-request source)
			// — same hygiene as "New chat". The adapter reads these stores when
			// the POST body is built, so clearing here strips the stale fields
			// from this send. Raw setState for the contextual store to skip
			// `onDismiss`'s focus-steal (see the reset effect). Skipped when this
			// send IS the pending context's own starter — a contextual `/`
			// landing during a blank slate must carry its fields.
			if (blankSlate && !sendOptions?.isEntryContextStarter) {
				if (useBizyContextualInsertStore.getState().pendingContext) {
					useBizyContextualInsertStore.setState({ pendingContext: null });
				}
				if (useBizyExternalOpenStore.getState().pendingRequest) {
					useBizyExternalOpenStore.getState().clearPendingRequest();
				}
			}
			if (blankSlate && !isContinuous) {
				setTitleOverride(undefined);
				sendMessageInNewConversation(content, extraContext);
			} else {
				sendMessage(content, extraContext);
			}
			dismissBlankSlate({ preserveScrollRoom: true });
			scroll.requestScrollToLatestMessage();
		},
		[blankSlate, isContinuous, sendMessage, sendMessageInNewConversation, dismissBlankSlate, scroll]
	);

	// `clearPendingContextualInsert` (store's `clearPendingContext`) is used by
	// the panel-close effect and UI block dispatcher.
	const clearPendingContextualInsert = useBizyContextualInsertStore((state) => state.clearPendingContext);
	// Pollinator "Add on" / "Comment" buttons enter a contextual comment
	// drafting session (so the surface sticks across follow-up turns).
	const setPendingContextualInsert = useBizyContextualInsertStore((state) => state.setPendingContext);

	const handleTranscript = useCallback((text: string) => {
		composerRef.current?.setInputText(text);
	}, []);
	const handleVoiceStart = useCallback(() => {
		track(AnalyticsEvent.Bizy.VoiceInputUsed, { path: window.location.pathname });
	}, [track]);
	const handleVoiceError = useCallback(
		(reason: string) => {
			track(AnalyticsEvent.Bizy.VoiceInputError, { reason, path: window.location.pathname });
		},
		[track]
	);
	const {
		isRecording: isVoiceRecording,
		isStarting: isVoiceStarting,
		startRecording: startVoice,
		stopRecording: stopVoice,
	} = useBizyVoiceInput({ onTranscript: handleTranscript, onStart: handleVoiceStart, onError: handleVoiceError });

	const lastUserMsgIndex = useMemo(() => {
		for (let i = messages.length - 1; i >= 0; i--) {
			if (messages[i].role === 'user') return i;
		}
		return -1;
	}, [messages]);

	useEffect(() => {
		const container = scroll.containerRef.current;
		const inner = container?.firstElementChild as HTMLElement | null;
		if (panelView !== 'chat') {
			scroll.cancelSmoothScroll();
			return;
		}
		if (!container || !inner) return;

		if (scroll.shouldScrollRef.current) {
			scroll.shouldScrollRef.current = false;
			const el = lastUserMsgRef.current;
			if (el) {
				inner.style.paddingBottom = `${container.clientHeight}px`;
				// Smooth scroll to the last user message.
				scroll.animateScrollTo(() => el.offsetTop - 36);
			}
		}

		const currentPadding = parseFloat(inner.style.paddingBottom) || 0;
		if (currentPadding > 0 && !scroll.isSmoothScrolling()) {
			const contentHeight = container.scrollHeight - currentPadding;
			const maxScrollWithoutPadding = contentHeight - container.clientHeight;
			const needed = Math.max(0, container.scrollTop - maxScrollWithoutPadding);
			inner.style.paddingBottom = `${needed}px`;
		}
	}, [messages, panelView, scroll]);

	// After the continuous-mode init load completes, scroll to the bottom so
	// the user sees the most recent turn rather than the oldest. In the
	// reload-landing case the idle clock fires in the same window; the jump is
	// skipped while the blank slate owns positioning (see jumpToBottom).
	useEffect(() => {
		if (!isContinuous || !didInitLoad) return;
		scroll.jumpToBottom();
	}, [isContinuous, didInitLoad, scroll]);

	const { data: recentData } = useBizyUserConversations({ per_page: RECENT_CONVERSATIONS_LIMIT }, { enabled: !isContinuous });
	const recentConversations = useMemo(() => (recentData?.[0] ?? []).slice(0, RECENT_CONVERSATIONS_LIMIT), [recentData]);

	// Set title from server/cache when conversationId changes or when we get new data for the same conversation (e.g. page
	// refresh: effect ran before recentConversations loaded; when list loads, allow filling in server title). Skip only when
	// same conversation and no title to apply, so we don't overwrite with nothing after a refetch.
	const prevConversationIdForTitleRef = useRef<string | undefined>();
	useEffect(() => {
		if (!conversationId) return;
		const activeConversation = recentConversations.find((c) => c.conversationId === conversationId);
		const cached = activeConversation ? getBizyConversationTitle(activeConversation) : getCachedTitle(conversationId);
		const conversationChanged = prevConversationIdForTitleRef.current !== conversationId;
		if (conversationChanged) {
			prevConversationIdForTitleRef.current = conversationId;
		} else if (!cached) {
			// Same conversation, no new title data — don't overwrite (avoids reverting to wrong title on refetch)
			return;
		}
		setTitleOverride(cached || undefined);
	}, [conversationId, recentConversations]);

	// Keep the conversations list fresh so the dropdown includes the current chat
	const prevConversationIdRef = useRef<string | undefined>();
	useEffect(() => {
		if (conversationId && conversationId !== prevConversationIdRef.current) {
			prevConversationIdRef.current = conversationId;
			queryClient.invalidateQueries(bizyUserConversationsQueryKey());
		}
	}, [conversationId, queryClient]);

	// For proactive (Bizy-started) conversations, the user's first reply is
	// often terse ("ok", "thanks", 👋) and the LLM has nothing to summarize
	// — historically that produced placeholder titles like "Pending
	// Conversation Title". Bizy's opener carries the real topic, so we
	// derive the title from the FIRST message in the transcript regardless
	// of role: the user's prompt for user-initiated chats, Bizy's opener
	// for proactive ones.
	const { firstUserMessage, titleSourceMessage, hasFirstAssistant } = useMemo(() => {
		let firstUser: string | undefined;
		let titleSource: string | undefined;
		let hasAssistant = false;
		for (const m of messages) {
			if (firstUser === undefined && m.role === 'user') firstUser = m.content;
			if (titleSource === undefined && m.content && m.content.length > 0) titleSource = m.content;
			if (!hasAssistant && m.role === 'assistant' && m.id) hasAssistant = true;
			if (firstUser !== undefined && titleSource !== undefined && hasAssistant) break;
		}
		return { firstUserMessage: firstUser, titleSourceMessage: titleSource, hasFirstAssistant: hasAssistant };
	}, [messages]);
	const generatedTitle = getActiveConversationTitle(firstUserMessage);
	const conversationTitle = titleOverride ?? generatedTitle;

	// Generate AI title after the first assistant response completes.
	// Skip if the conversation already has a server-side or cached title.
	const hasRequestedTitleRef = useRef<string | undefined>();
	const manuallyRenamedRef = useRef(false);
	const conversationIdRef = useRef(conversationId);
	conversationIdRef.current = conversationId;

	useEffect(() => {
		if (!conversationId || !titleSourceMessage || titleOverride) return;
		if (hasRequestedTitleRef.current === conversationId) return;

		// We still gate on the first assistant reply existing so the LLM
		// has the substantive part of the exchange to draw from. For
		// proactive conversations that's also the title source; for
		// user-initiated ones it ensures we don't fire on a one-shot user
		// prompt that hasn't produced a response yet.
		if (!hasFirstAssistant) return;

		// Never overwrite a title the server already curated — e.g. a proactive
		// "dreaming" opener the backend titled "Something I noticed about you
		// and X".
		const activeConversationForTitle = recentConversations.find((c) => c.conversationId === conversationId);
		if (activeConversationForTitle?.conversationTitle) return;

		// A server-curated proactive title only reaches the client via the
		// recents list, so for a proactive opener (no user message yet) wait
		// until this conversation has loaded there before deriving a title from
		// Bizy's own opener — otherwise we'd race ahead and PATCH over the
		// server title. A freshly delivered proactive conversation is the
		// newest, so it lands in the recents window promptly; the trade-off is
		// that an older proactive opener reopened from history with no server
		// title keeps its time-of-day label until the user replies.
		if (firstUserMessage === undefined && !activeConversationForTitle) return;

		const requestConversationId = conversationId;
		hasRequestedTitleRef.current = conversationId;
		manuallyRenamedRef.current = false;
		generateTitle(
			{ message: titleSourceMessage },
			{
				onSuccess: (data) => {
					if (data.title && !manuallyRenamedRef.current && conversationIdRef.current === requestConversationId) {
						setTitleOverride(data.title);
						setCachedTitle(requestConversationId, data.title);
						updateConversation({ conversationId: requestConversationId, title: data.title });
						track(AnalyticsEvent.Bizy.ConversationTitleGenerated, { path: window.location.pathname });
					}
				},
			}
		);
	}, [conversationId, titleSourceMessage, titleOverride, hasFirstAssistant, firstUserMessage, recentConversations, generateTitle, updateConversation, track]);

	const company = useCurrentCompany();
	const isBonuslyCompany = useIsBonuslyCompany();
	const isCanceled = company.status === 'non_renewing';
	const isAdmin = user.admin;
	// Admin-only Bizy: feature is enabled for admins but turned off for the rest
	// of the company. Only surface the notice to admins (who can change it).
	const featureStates = company.feature_states;
	const isBizyAdminOnly = Boolean(isAdmin && featureStates?.bizy_for_admins && featureStates?.bizy_for_employees === false);
	const segment = getAudienceSegment(company.status, isAdmin);
	const isTrial = isTrialSegment(segment);
	const { data: activationState } = useBizyActivationState(isTrial);
	const bizyForRewardsEnabled = useFeatureFlag('bizy-for-rewards');
	const chipContext: ChipContext | undefined = useMemo(
		() =>
			activationState && {
				...activationState,
				user: { ...activationState.user, earning_balance: user.earning_balance ?? 0 },
				bizy_for_rewards_enabled: bizyForRewardsEnabled,
			},
		[activationState, user.earning_balance, bizyForRewardsEnabled]
	);
	const canAccessUserManagement = usePermission(Permission.CanAccessUserManagement);
	const chipPromptName = partnerNameFromPageName(pageContext.pageName) ?? 'someone on my team';

	const isPanelOpen = useBizySidePanelStore((state) => state.isOpen);
	const isFullscreen = useBizySidePanelStore((state) => state.isFullscreen);
	const isHistoryDrawerOpen = useBizySidePanelStore((state) => state.isHistoryDrawerOpen);
	const isPeopleBrowserCollapsed = useBizySidePanelStore((state) => state.isPeopleBrowserCollapsed);
	const togglePeopleBrowserCollapsed = useBizySidePanelStore((state) => state.togglePeopleBrowserCollapsed);
	const toggleFullscreen = useBizySidePanelStore((state) => state.toggleFullscreen);
	const lastTrigger = useBizySidePanelStore((state) => state.lastTrigger);
	const closePanel = useBizySidePanelStore((state) => state.close);
	const showSettings = useBizySidePanelStore((state) => state.showSettings);

	// Nickname-preferring greeting name, shared with Bizy Desktop. Fetched only
	// while the panel is open.
	const displayName = useBizyGreetingDisplayName(bizyGreetingFallbackName(user), { enabled: isPanelOpen });

	// Prompt library: permissions, connector queries, favourites, model selector, handlers and modal.
	// Called here — before useBizyWhatsNewTip — because personalizedPromptSuggestions and
	// isPromptSuggestionsReady must be available to the tip hook below.
	const handlePromptLibrarySend = useCallback(
		(message: string, extraContext?: Record<string, string>) => {
			sendFromFreshStartSurface(message, extraContext);
			requestAnimationFrame(() => composerRef.current?.focusInput());
		},
		[sendFromFreshStartSurface]
	);
	const handleAfterPromptSelect = useCallback(() => {
		scroll.requestScrollToLatestMessage();
		requestAnimationFrame(() => composerRef.current?.focusInput());
	}, [scroll]);
	const {
		footerContent: promptLibraryFooterContent,
		modal: promptLibraryModal,
		personalizedPromptSuggestions,
		isPromptSuggestionsReady,
	} = useBizyPromptLibrary({
		pageType,
		pageName: pageContext.pageName,
		isDisabled: inputDisabled,
		onSendMessage: handlePromptLibrarySend,
		onAfterSelect: handleAfterPromptSelect,
		onShowSettings: (tab) => showSettings(tab as Parameters<typeof showSettings>[0]),
		modelSelector: isBizyModelExperimentEnabled ? { selectedModel: selectedBizyModel, onSelectModel: handleSelectBizyModel } : undefined,
	});

	const pendingContextualInsert = useBizyContextualInsertStore((state) => state.pendingContext);
	const markPendingContextStarterSent = useBizyContextualInsertStore((state) => state.markPendingContextStarterSent);
	const triggerElementRef = useRef<HTMLElement | null>(null);
	const prevIsPanelOpenRef = useRef(isPanelOpen);
	const prevPanelViewRef = useRef(panelView);
	const prevIsLoadingConversationRef = useRef(isLoadingConversation);
	const isEmptyState = messages.length === 0 && !isLoading;
	// Fresh-start surfaces (greeting cluster, starter chips) show on the true
	// empty state AND inside the idle blank slate, where messages are still
	// rendered above the divider.
	const showFreshStart = isEmptyState || isBlankSlate;
	const fullscreenFontSize = isFullscreen ? 'md' : undefined;
	const chatTextFontSize = isFullscreen ? 'md' : ({ base: 'md', sm: 'sm' } as const);

	useEffect(() => {
		onConversationIdChange?.(conversationId);
	}, [conversationId, onConversationIdChange]);

	// When the panel closes (any reason), drop any pending contextual `/` invocation
	// so the next open starts clean. `onDismiss` returns focus to the original input
	// (see use-bizy-contextual-insert-store).
	const prevIsPanelOpenForContextRef = useRef(isPanelOpen);
	useEffect(() => {
		const wasOpen = prevIsPanelOpenForContextRef.current;
		prevIsPanelOpenForContextRef.current = isPanelOpen;
		if (wasOpen && !isPanelOpen && pendingContextualInsert) {
			track(AnalyticsEvent.Bizy.ContextualDismissed, {
				surface: pendingContextualInsert.surface,
				path: window.location.pathname,
			});
			clearPendingContextualInsert();
		}
	}, [isPanelOpen, pendingContextualInsert, clearPendingContextualInsert, track]);

	// Auto-send the contextual starter on open so Bizy actively starts the
	// conversation (asks the right follow-up question) instead of waiting
	// silent. Dedup in the store, not in a component ref, so layout remounts
	// from resizing/fullscreen changes cannot replay the same contextual `/`.
	useEffect(() => {
		if (!pendingContextualInsert) return;
		if (!isPanelOpen) return;
		const currentContext = useBizyContextualInsertStore.getState().pendingContext;
		if (!currentContext || currentContext !== pendingContextualInsert || currentContext.hasSentStarter) return;
		markPendingContextStarterSent(currentContext);
		// A contextual `/` invocation opens a new drafting topic — treat it as a
		// fresh-start send if the blank slate is active. This send is the
		// pending context's own starter, so the blank-slate branch must not
		// clear it out from under its POST.
		sendFromFreshStartSurface(currentContext.starterMessage, undefined, { isEntryContextStarter: true });
	}, [pendingContextualInsert, isPanelOpen, markPendingContextStarterSent, sendFromFreshStartSurface]);

	// Typed UI block action dispatcher. Passed to the shared
	// `DocumentRenderer` below via its `dispatchUiBlockAction` prop so that
	// any card renderer (recognition draft, comment draft, recognition view,
	// ...) can dispatch typed actions (`insert_into_draft`, `send_now`,
	// `open_in_givebox`, `view_post`, `dismiss`) without the chat view
	// having to thread surface-specific callbacks through every message.
	const navigate = useNavigate();
	// `getCommentComposer` reads from the active-composers store at
	// CALL time (not subscription time) so the dispatcher always sees
	// the latest mounted composer for a bonus, even if it remounted
	// after the dispatcher was built. `queuePendingCommentInsert` is
	// the fallback the dispatcher uses when no composer is mounted —
	// it stashes the formatted draft + the dispatcher then navigates
	// to /bonuses/{id}, where BonusContent auto-opens the editor and
	// the freshly-mounted CommentComposer drains the queue. Both reads
	// snapshot from `getState()` so the dispatcher's stable identity
	// doesn't depend on a Zustand subscription.
	const getCommentComposer = useBizyActiveComposersStore.getState().getCommentComposer;
	const queuePendingCommentInsert = useBizyActiveComposersStore.getState().queuePendingInsert;
	// Dispatcher-initiated sends (send_now / commit_draft / attach_gif …) and
	// card follow-ups/confirms/drills must scroll to the new turn just like a
	// typed message does — the raw sendMessage doesn't request the scroll, so
	// wrap it here. These sends engage the conversation already on screen, so
	// they exit the blank slate and continue it rather than starting fresh.
	const sendChatMessageWithScroll = useCallback(
		(content: string, extraContext?: Record<string, string>, displayOptions?: { attachmentPreviewUrl?: string; userNodes?: BizyUserMessageNode[] }) => {
			dismissBlankSlate({ preserveScrollRoom: true });
			sendMessage(content, extraContext, displayOptions);
			scroll.requestScrollToLatestMessage();
		},
		[sendMessage, dismissBlankSlate, scroll]
	);

	const pendingChatMessage = useBizyPendingChatMessageStore((state) => state.pendingMessage);
	const clearPendingChatMessage = useBizyPendingChatMessageStore((state) => state.clearPendingMessage);
	const uiBlockActionDispatcher = useBuildBizyUiBlockActionDispatcher({
		pendingContextualInsert,
		clearPendingContextualInsert,
		closePanel,
		navigate,
		currentConversationId: conversationId,
		getCommentComposer,
		queuePendingCommentInsert,
		sendChatMessage: sendChatMessageWithScroll,
		setPendingContext: setPendingContextualInsert,
	});

	// Environment-specific handlers the agnostic @bonusly/bizy-kit
	// DocumentRenderer delegates to (links, analytics, gif search, user
	// hydration, give-box hand-off). Built once and threaded into the renderer.
	const bizyKitRendererProps = useBizyKitRendererProps();

	// The external-open "start new conversation" confirm is a full reset
	// (resetChat clears the adapter's archive itself), so the blank slate
	// must go with it.
	const resetChatForExternalOpen = useCallback(() => {
		dismissBlankSlate();
		resetChat();
	}, [dismissBlankSlate, resetChat]);

	const {
		pendingRequest: pendingFeatureRequest,
		showConflictDialog: showExternalOpenConflict,
		handleConfirmNewConversation,
		handleKeepCurrentConversation,
	} = useBizyExternalOpenAutoSend({
		isPanelOpen,
		isEmptyState,
		// The continue wrapper: external-open sends target the on-screen
		// conversation (empty-chat auto-send, deep link into the active
		// conversation), so they dismiss any blank slate rather than archive,
		// and the wrapper owns the scroll to the new turn.
		sendMessage: sendChatMessageWithScroll,
		resetChat: resetChatForExternalOpen,
		loadConversation,
		currentConversationId: conversationId,
		isLoadingConversation,
		setInputValue: setComposerText,
	});

	const conversationPathRef = useRef<string | null>(null);
	const [showPagePrompts, setShowPagePrompts] = useState(false);
	useEffect(() => {
		if (isEmptyState) {
			setShowPagePrompts(false);
			conversationPathRef.current = null;
			return;
		}
		if (conversationPathRef.current === null) {
			conversationPathRef.current = window.location.pathname;
		}
		setShowPagePrompts(window.location.pathname !== conversationPathRef.current);
	}, [isEmptyState, pageContext]);

	const prevConversationIdForPromptsRef = useRef(conversationId);
	useEffect(() => {
		const prev = prevConversationIdForPromptsRef.current;
		prevConversationIdForPromptsRef.current = conversationId;
		if (prev && conversationId && prev !== conversationId) {
			conversationPathRef.current = null;
			setShowPagePrompts(false);
		}
	}, [conversationId]);

	// The 1:1 list page no longer pops a Bizy self-introduction (BIZY-413): navigating to
	// the meet page must not auto-introduce Bizy to returning users. The page now falls
	// through to the standard empty state (greeting context + prompts) like every other
	// surface. We still track it so the admin first-launch intro stays suppressed here —
	// preserving prior behavior, where the meet intro took precedence on this page.
	const isOneOnOneListPage = pageType === '1on1_list';

	// ── New-segment greeter callout (BIZY-638) ────────────────────────
	// When a fresh chat segment is about to start (server 8h boundary) and the
	// member has unread People Browser moments, show a friendly starter callout
	// at the bottom of the timeline instead of the plain empty-state greeting.
	const isPeopleBrowserEnabled = usePeopleBrowserEnabled();
	const { entries: greeterEntries, unreadEntries, unreadCount: greeterUnreadCount } = useBizyPeopleEntries(isPeopleBrowserEnabled);
	const { connected: connectedConnectorCount } = useBizyConnectorCounts();
	const {
		showGreeter,
		timeOfDayKey: greeterTimeOfDayKey,
		dismiss: dismissGreeter,
	} = useBizyGreeterCallout({
		enabled: isPeopleBrowserEnabled,
		isNewSegment,
		unreadCount: greeterUnreadCount,
		momentCount: greeterEntries.length,
		connectedConnectorCount,
	});

	// Consume a message queued from the People Browser "Let's dive in" (BIZY-651):
	// send it with its moment context + display node, and dismiss the greeter
	// (the send starts the segment). Sends only while the panel is open; either
	// way the pending message is cleared so it can't survive a close.
	useEffect(() => {
		if (!pendingChatMessage) return;
		if (isPanelOpen) {
			dismissGreeter();
			sendChatMessageWithScroll(pendingChatMessage.text, pendingChatMessage.extraContext, {
				userNodes: pendingChatMessage.userNode ? [pendingChatMessage.userNode] : undefined,
			});
		}
		clearPendingChatMessage();
	}, [pendingChatMessage, isPanelOpen, dismissGreeter, sendChatMessageWithScroll, clearPendingChatMessage]);

	// Timestamp dividers between message bursts (BIZY-638). The helper decides
	// where a divider goes; <RelativeTime> renders the localized label. Gated
	// with the rest of the People Browser work.
	const messageDividers = useMemo(() => (isPeopleBrowserEnabled ? getMessageDividers(messages) : new Map<number, Date>()), [isPeopleBrowserEnabled, messages]);

	// The same dividers inside archived segments (BIZY-683): recomputed per
	// segment so burst/day breaks that were visible before the idle boundary
	// survive archiving, with the same People Browser gating as the live list.
	const archivedSegmentDividers = useMemo(
		() => (isPeopleBrowserEnabled ? archivedSegments.map((segment) => getMessageDividers(segment.messages)) : []),
		[isPeopleBrowserEnabled, archivedSegments]
	);

	// Arm the idle trigger only while there's a settled conversation on screen.
	// The BIZY-638 new-segment greeter wins when active (it already presents a
	// fresh-start surface), as does the timeout retry UI. In continuous mode,
	// wait for the init load so the trigger evaluates against the real
	// timeline, not a half-hydrated one.
	const isIdleBlankSlateArmed =
		isPanelOpen &&
		panelView === 'chat' &&
		!showGreeter &&
		!isBlankSlate &&
		messages.length > 0 &&
		(!isContinuous || didInitLoad) &&
		!isLoading &&
		!isTimedOut &&
		!isOtherTabLoading &&
		!isLoadingConversation &&
		!isVoiceRecording &&
		!isVoiceStarting;

	// Latest messages for enterBlankSlate, which is called from window-event
	// listeners that outlive any single render.
	const messagesRef = useRef(messages);
	messagesRef.current = messages;

	const enterBlankSlate = useCallback(() => {
		const container = scroll.containerRef.current;
		// The blank-slate region supplies the scroll room from here on; drop any
		// leftover bottom padding from a previous send-scroll.
		const inner = container?.firstElementChild as HTMLElement | null;
		if (inner) inner.style.paddingBottom = '0px';
		setBlankSlate({
			// The divider caps the OLD conversation, so it's labeled with the
			// conversation's last stamped turn — not when the fresh start
			// appeared (a day-old thread must not read "a minute ago").
			boundaryAt: newestMessageTimestamp(messagesRef.current) ?? new Date(),
			// Estimate; refined from the divider's measured height once it paints
			// (see the positioning effect below).
			minHeight: container ? Math.max(0, container.clientHeight - BLANK_SLATE_VIEWPORT_INSET) : 0,
		});
	}, [scroll]);

	useBizyIdleBlankSlate({
		enabled: isIdleBlankSlateArmed,
		isComposing: isComposerDrafting,
		onIdle: enterBlankSlate,
	});

	// Position the blank slate once its divider + region are in the DOM:
	// refine the region's min-height from the divider's real rendered height
	// (the pre-paint estimate can't measure it), then animate so the divider
	// sits BLANK_SLATE_SCROLL_OFFSET below the container top with the tail of
	// the previous exchange visible above it. Runs once per blank-slate entry;
	// the animation's per-frame target getter absorbs layout shifts while it
	// runs, and native scroll anchoring holds the position afterwards.
	useEffect(() => {
		if (!isBlankSlate) return;
		const frame = requestAnimationFrame(() => {
			const container = scroll.containerRef.current;
			const dividerEl = blankSlateDividerRef.current;
			if (!container || !dividerEl) return;
			// With the measured divider height, max-scroll geometry places the
			// divider exactly at the offset when the region bottoms out — the
			// animation target and the scroll clamp agree instead of fighting.
			const measuredInset = BLANK_SLATE_SCROLL_OFFSET + dividerEl.offsetHeight + TIMELINE_ROW_GAP_PX;
			setBlankSlate((current) => {
				if (!current) return current;
				const minHeight = Math.max(0, container.clientHeight - measuredInset);
				return minHeight === current.minHeight ? current : { ...current, minHeight };
			});
			scroll.animateScrollTo(() => dividerEl.offsetTop - BLANK_SLATE_SCROLL_OFFSET);
		});
		return () => cancelAnimationFrame(frame);
	}, [isBlankSlate, scroll]);

	// The blank slate can't outlive the state it was entered from. One guard
	// covers every path that invalidates it: a send or retry starting
	// (isLoading — sends from the blank slate's own surfaces already dismissed
	// synchronously, so this is a no-op there), a classic conversation load
	// (isLoadingConversation), the timeline emptying out-of-band (pagehide
	// reset + bfcache restore), the panel closing (the next open re-evaluates
	// staleness from scratch), and the new-segment greeter winning (BIZY-708) —
	// it can flip on asynchronously after the blank slate was entered, so it
	// tears the blank slate down rather than render both fresh-start surfaces.
	useEffect(() => {
		if (!isBlankSlate) return;
		if (isLoading || isLoadingConversation || messages.length === 0 || !isPanelOpen || showGreeter) dismissBlankSlate();
	}, [isBlankSlate, isLoading, isLoadingConversation, messages, isPanelOpen, showGreeter, dismissBlankSlate]);

	const isGreetingContextVisible = showFreshStart && isPanelOpen && panelView === 'chat' && !showGreeter;
	const { variant: greetingVariant, impressionCount } = useBizyGreeting(isGreetingContextVisible, isCanceled, isAdmin, {
		// Blank-slate showings display the greeting but only true empty-state
		// openings advance the intro → familiar impression window.
		countsImpression: !isBlankSlate,
	});
	// Blank slates continue an existing conversation — never show first-launch
	// copy there: map `introductory` to `familiar` for display (the canceled
	// variants still show; they carry account state, not first-launch copy).
	// Computing this at element-build time is safe because the two greeting
	// slots are mutually exclusive: the empty-state slot needs
	// messages.length === 0, and every state path that empties the timeline
	// (new chat, external-open reset, fresh-start send, conversation load)
	// also clears the blank slate.
	const displayGreetingVariant = isBlankSlate && greetingVariant === 'introductory' ? 'familiar' : greetingVariant;
	// The familiar greeting draws from a rotating pool, re-rolled per showing.
	const familiarGreetingText = useBizyFamiliarGreeting(displayName, isGreetingContextVisible);
	const greetingKey = `greeting.${
		displayGreetingVariant === 'canceled_admin'
			? 'canceledAdmin'
			: displayGreetingVariant === 'canceled_non_admin'
				? 'canceledNonAdmin'
				: displayGreetingVariant
	}`;
	const greetingText = displayGreetingVariant === 'familiar' ? familiarGreetingText : t(greetingKey, { name: displayName });
	// Show the full admin capabilities block for the same impressions window as the
	// introductory greeting (0-3). After 4 impressions, admins fall through to the
	// familiar short greeting like everyone else.
	//
	// Gated to the admin roles whose work the intro copy actually speaks to (program
	// data, reports, financials, rewards, awards). Intentionally excludes Tech,
	// Announcements, and other role admins — `isAdmin` (Global-only) is still used
	// elsewhere in this file for the admin-only Bizy banner, canceled greeting
	// variant, prompt segment, and prompt chips, which should stay Global-scoped.
	//
	// `user.admin || user.global_admin` mirrors the canonical Global check in
	// `permission-validations.ts` `Permission.CanViewAdmin`. The two fields resolve
	// from the same backend source in `UserProfileBlueprint` but can diverge in
	// test fixtures and sample data, so we OR them defensively.
	const canSeeAdminIntro = user.admin || user.global_admin || user.finance_admin || user.rewards_admin || user.awards_admin || user.reports_admin;
	// Trial admins get the trial welcome empty state, not the admin first-launch
	// intro — its "make sense of your data / participation, trends" framing is the
	// same mature-program coaching we're keeping away from trials (see RR-102).
	// Never in the blank slate either: first-launch copy doesn't belong in a
	// mid-conversation fresh start.
	const showAdminIntro = canSeeAdminIntro && !isTrial && !isOneOnOneListPage && !isBlankSlate && greetingVariant === 'introductory';

	const hasTrackedGreetingRef = useRef(false);
	useEffect(() => {
		if (isGreetingContextVisible && !hasTrackedGreetingRef.current) {
			hasTrackedGreetingRef.current = true;
			track(AnalyticsEvent.Bizy.GreetingShown, {
				// The variant actually displayed (blank slates map introductory →
				// familiar), so analytics reflect what the user saw.
				variant: displayGreetingVariant,
				impressionCount,
				path: window.location.pathname,
			});
		}
		if (!isGreetingContextVisible) {
			hasTrackedGreetingRef.current = false;
		}
	}, [isGreetingContextVisible, displayGreetingVariant, impressionCount, track]);

	// What's New tips (personalized for all users; static rotation for admins)
	const {
		tip: whatsNewTip,
		dismiss: dismissWhatsNewTip,
		shouldShowTip: showWhatsNewTip,
		hasActiveTip: hasActiveWhatsNewTip,
		dateKey: whatsNewDateKey,
	} = useBizyWhatsNewTip({
		canShowStaticTips: isAdmin,
		// True empty state only — the idle blank slate stays minimalist (mascot
		// + familiar greeting) and never surfaces a tip, so tip impressions and
		// dismissals can't fire from blank-slate showings.
		isEmptyState,
		isPanelOpen,
		personalizedPrompts: personalizedPromptSuggestions,
		personalizedPromptsResolved: isPromptSuggestionsReady,
		// Trial admins get the trial welcome + starter prompts instead of the
		// "What's new for admins" tips, which assume a mature program with data.
		isTrialCompany: isTrial,
	});

	const hasTrackedWhatsNewTipRef = useRef(false);
	useEffect(() => {
		if (showWhatsNewTip && whatsNewTip && !hasTrackedWhatsNewTipRef.current) {
			hasTrackedWhatsNewTipRef.current = true;
			track(AnalyticsEvent.Bizy.WhatsNewTipShown, {
				tipId: whatsNewTip.id,
				path: window.location.pathname,
			});
		}
		if (!showWhatsNewTip) {
			hasTrackedWhatsNewTipRef.current = false;
		}
	}, [showWhatsNewTip, whatsNewTip, track]);

	// Dismiss the What's New tip when the user engages with the chat by loading
	// a past conversation from history (or starting a new one). Gated on
	// `hasActiveWhatsNewTip` rather than `showWhatsNewTip` because loading a
	// past chat with messages sets `conversationId` and flips `isEmptyState`
	// false in the same render — `showWhatsNewTip` is already false by the
	// time this effect runs, but the underlying tip would otherwise re-appear
	// the next time the admin returns to the empty state today.
	const prevConversationIdForTipDismissRef = useRef(conversationId);
	useEffect(() => {
		const prev = prevConversationIdForTipDismissRef.current;
		prevConversationIdForTipDismissRef.current = conversationId;
		if (!prev && conversationId && hasActiveWhatsNewTip) {
			track(AnalyticsEvent.Bizy.WhatsNewTipDismissed, {
				tipId: whatsNewTip?.id,
				reason: 'conversation_loaded',
				path: window.location.pathname,
			});
			dismissWhatsNewTip();
		}
	}, [conversationId, hasActiveWhatsNewTip, whatsNewTip, dismissWhatsNewTip, track]);

	const personalizedChipSuggestion = useMemo(
		() => pickRandomPersonalizedChipPrompt(personalizedPromptSuggestions, buildPersonalizedGenerationKey(personalizedPromptSuggestions), whatsNewDateKey),
		[personalizedPromptSuggestions, whatsNewDateKey]
	);

	const promptChipEntries = useMemo<PromptChipEntry[]>(
		() => {
			const chipCount = isTrialSegment(segment) ? 5 : 3;
			const personalizedEntries = personalizedChipSuggestion
				? [
						{
							key: personalizedChipSuggestion.id,
							prompt: personalizedChipSuggestion.prompt,
							categoryId: 'personalized' as const,
						},
					]
				: [];
			const pageEntries = getPromptChips(pageType, segment, chipContext, isAdmin, Math.max(0, chipCount - personalizedEntries.length), pageContext.pageData, {
				canAccessUserManagement,
			}).map((key) => ({
				key,
				prompt: t(key, { name: chipPromptName }),
			}));

			return [...personalizedEntries, ...pageEntries];
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[pageType, segment, chipContext, isAdmin, chipPromptName, pageContext.pageData, canAccessUserManagement, personalizedChipSuggestion]
	);
	const promptChips = useMemo(
		() =>
			promptChipEntries.map((entry) => ({
				prompt: entry.prompt,
				expandable: entry.categoryId === 'personalized',
			})),
		[promptChipEntries]
	);
	const pageOnlyPromptChips = useMemo(
		() => promptChipEntries.filter((entry) => entry.categoryId !== 'personalized').map((entry) => ({ prompt: entry.prompt, expandable: false })),
		[promptChipEntries]
	);
	const [isScrolledFromTop, setIsScrolledFromTop] = useState(false);

	useEffect(() => {
		if ((!isPanelOpen || panelView !== 'chat') && (isVoiceRecording || isVoiceStarting)) {
			void stopVoice();
		}
	}, [isPanelOpen, panelView, isVoiceRecording, isVoiceStarting, stopVoice]);

	useEffect(() => {
		if (isPanelOpen && panelView === 'chat') return;

		cancelBizyHold();
		dismissBizyGamePrompt();
		closeBizyGame();
	}, [cancelBizyHold, closeBizyGame, dismissBizyGamePrompt, isPanelOpen, panelView]);

	useLayoutEffect(() => {
		const wasOpen = prevIsPanelOpenRef.current;
		prevIsPanelOpenRef.current = isPanelOpen;

		if (isPanelOpen && !wasOpen) {
			triggerElementRef.current = document.activeElement as HTMLElement | null;
			if ((lastTrigger === 'user' || lastTrigger === 'keyboard') && panelView === 'chat' && !isBizyGameOpen) {
				composerRef.current?.focusInput();
			}
		} else if (!isPanelOpen && wasOpen) {
			if (lastTrigger === 'user' || lastTrigger === 'keyboard') {
				triggerElementRef.current?.focus();
			}
			triggerElementRef.current = null;
		}
	}, [isBizyGameOpen, isPanelOpen, lastTrigger, panelView]);

	useEffect(() => {
		const returnedToChat = prevPanelViewRef.current !== 'chat' && panelView === 'chat';
		const finishedLoading = prevIsLoadingConversationRef.current && !isLoadingConversation;
		prevPanelViewRef.current = panelView;
		prevIsLoadingConversationRef.current = isLoadingConversation;

		if ((returnedToChat || finishedLoading) && isPanelOpen && !isBizyGameOpen) {
			// jumpToBottom itself skips the jump while the blank slate owns
			// positioning (checked at rAF time).
			if (finishedLoading) scroll.jumpToBottom();
			requestAnimationFrame(() => {
				composerRef.current?.focusInput();
			});
		}
	}, [panelView, isPanelOpen, isLoadingConversation, isBizyGameOpen, scroll]);

	const lastMessage = messages[messages.length - 1];
	const isStreaming = isLoading && lastMessage?.role === 'assistant' && !lastMessage.id;
	const showThinkingIndicator = isLoading && !isStreaming && lastMessage?.role !== 'assistant';
	// On peek-triggered opens, auto-load the conversation. For toggle / already-
	// open paths, leave it to the empty-state greeting to surface the new
	// message; user clicks the wave to engage.
	const { showGreeting, engageWithGreeting, acknowledgeUnread } = useLoadProactiveConversationOnOpen({
		loadConversation,
		// On ANY open path with an unread (page refresh, preference-restore,
		// manual toggle, …) auto-load the proactive conversation. We used
		// to gate this on `isEmptyState` so an active prior conversation
		// stayed untouched, but a user loading into Bonusly with both a
		// previous conv and a new proactive notification would silently
		// land on the prior conv and have to find the new one in the
		// dropdown. Mid-session yank is prevented by:
		//   - the snapshot pattern (autoLoadOnOpen captured at open),
		//   - `passiveAutoLoadFiredThisOpenRef` (one-shot per session),
		//   - `engagementSuppressesPassiveRef` (any explicit engagement
		//     suppresses subsequent passive loads).
		autoLoadOnOpen: true,
	});
	const showNotificationGreeting = showGreeting;

	useEffect(() => {
		if (!showNotificationGreeting) return;
		dismissBizyGamePrompt();
		closeBizyGame();
	}, [closeBizyGame, dismissBizyGamePrompt, showNotificationGreeting]);

	useEffect(() => {
		if (!isBizyGamePromptOpen) return;

		const handlePointerDown = (event: globalThis.PointerEvent) => {
			if (event.target instanceof HTMLElement && event.target.closest('[data-bizy-game-invite]')) return;
			track(AnalyticsEvent.Bizy.BizySkyHighInviteAction, {
				action: 'outside_dismiss',
				path: window.location.pathname,
			});
			dismissBizyGamePrompt();
		};

		document.addEventListener('pointerdown', handlePointerDown);
		return () => document.removeEventListener('pointerdown', handlePointerDown);
	}, [dismissBizyGamePrompt, isBizyGamePromptOpen, track]);

	useEffect(() => {
		if (!isBizyGamePromptOpen || showNotificationGreeting) {
			hasTrackedBizyGameInviteShownRef.current = false;
			return;
		}
		if (hasTrackedBizyGameInviteShownRef.current) return;

		hasTrackedBizyGameInviteShownRef.current = true;
		track(AnalyticsEvent.Bizy.BizySkyHighInviteShown, {
			path: window.location.pathname,
		});
	}, [isBizyGamePromptOpen, showNotificationGreeting, track]);

	const handleBizyGameInvitePlay = useCallback(() => {
		track(AnalyticsEvent.Bizy.BizySkyHighInviteAction, {
			action: 'play',
			path: window.location.pathname,
		});
		openBizyGame();
	}, [openBizyGame, track]);

	const handleBizyGameInviteDismiss = useCallback(() => {
		track(AnalyticsEvent.Bizy.BizySkyHighInviteAction, {
			action: 'dismiss',
			path: window.location.pathname,
		});
		dismissBizyGamePrompt();
	}, [dismissBizyGamePrompt, track]);

	const handleBizyClick = useCallback(() => {
		triggerBizyGiggle();
		if (showNotificationGreeting) {
			// Engaging the proactive message pulls its turns onto the timeline —
			// leave the blank slate so the user lands on them.
			dismissBlankSlate();
			engageWithGreeting();
		}
	}, [engageWithGreeting, showNotificationGreeting, triggerBizyGiggle, dismissBlankSlate]);

	const handleBizyPointerDown = useCallback(
		(event: PointerEvent<HTMLDivElement>) => {
			if (showNotificationGreeting) return;
			event.currentTarget.setPointerCapture?.(event.pointerId);
			startBizyHold();
		},
		[showNotificationGreeting, startBizyHold]
	);

	const handleBizyPointerEnd = useCallback(() => {
		cancelBizyHold();
	}, [cancelBizyHold]);

	const handleBizyKeyDown = useCallback(
		(event: KeyboardEvent<HTMLDivElement>) => {
			if (event.key !== 'Enter' && event.key !== ' ') return;
			event.preventDefault();
			if (showNotificationGreeting) {
				handleBizyClick();
				return;
			}
			if (event.repeat) return;
			startBizyHold();
		},
		[handleBizyClick, showNotificationGreeting, startBizyHold]
	);

	const handleBizyKeyUp = useCallback(
		(event: KeyboardEvent<HTMLDivElement>) => {
			if (event.key !== 'Enter' && event.key !== ' ') return;
			event.preventDefault();
			if (!showNotificationGreeting) cancelBizyHold();
		},
		[cancelBizyHold, showNotificationGreeting]
	);

	// When a proactive notification surfaces while the panel is already open
	// and the user is in a conversation, the in-chat Bizy waves + wiggles to
	// call attention to it. Hovering anywhere in the panel acknowledges the
	// attention so the animation stops (without firing markSeen — that only
	// happens when they click the Bizy to engage).
	//
	// We don't try to re-trigger the wiggle on a "second notification arrives
	// mid-session" boolean edge: the trigger pipeline cooldown spaces
	// notifications per user by days, so multiple notifications landing in a
	// single open session is not a real scenario.
	const [hasAcknowledgedAttention, setHasAcknowledgedAttention] = useState(false);

	const isCallingAttentionInChat = showNotificationGreeting && !hasAcknowledgedAttention && !isEmptyState;
	const bizyState: BizyState = isStreaming
		? 'pointingLeft'
		: isLoading
			? 'thinkingLeft'
			: isCallingAttentionInChat
				? 'waving'
				: isBizyTickled
					? 'tickleLeft'
					: (bizyEmotion ?? 'curious');
	const bizyHoldSpinProgress = isBizyGameHolding ? Math.max(0, (bizyGameHoldProgress - 0.05) / 0.95) : 0;
	const bizyHoldSpinAnimation = { x: 0, y: 0, rotate: -360 * bizyHoldSpinProgress, scale: 1 };
	const bizyHoldSpinTransition = { duration: isBizyGameHolding ? 0.08 : 0.18, ease: 'linear' as const };
	// NOTE: the empty-state Bizy reuses `bizyState` directly. The
	// "doesn't wave for attention" semantics it documents are already
	// enforced by the `!isEmptyState` guard inside isCallingAttentionInChat
	// above — the navbar badge is the persistent signal, and any other
	// engagement entry point (clicking the Bizy, focusing the input,
	// clicking Ask Bizy) still fires markSeen.
	const emptyChatBizyAnchorRef = useBizyFlightAnchor('chat');
	const activeChatBizyAnchorRef = useBizyFlightAnchor('chat');
	const isChatBizyHidden = useIsBizyFlightAnchorHidden('chat');

	const thinkingPhrases = useMemo(() => {
		const raw = t('thinkingPhrases', { returnObjects: true });
		return Array.isArray(raw) ? (raw as string[]) : [t('thinking')];
	}, [t]);
	const [thinkingPhraseIdx, setThinkingPhraseIdx] = useState(() => Math.floor(Math.random() * thinkingPhrases.length));
	useEffect(() => {
		if (!isLoading || thinkingPhrases.length <= 1) {
			setThinkingPhraseIdx((prev) => {
				if (thinkingPhrases.length <= 1) return 0;
				let next = Math.floor(Math.random() * (thinkingPhrases.length - 1));
				if (next >= prev) next++;
				return next;
			});
			return;
		}
		const id = setInterval(() => {
			setThinkingPhraseIdx((prev) => {
				let next = Math.floor(Math.random() * (thinkingPhrases.length - 1));
				if (next >= prev) next++;
				return next;
			});
		}, 4000);
		return () => clearInterval(id);
	}, [isLoading, thinkingPhrases.length]);

	const toolProgressItems = activeToolCalls
		.map(getToolProgressKey)
		.filter((key): key is string => key !== null)
		.map((key) => t(key));

	const lastCompleteAssistantIndex = useMemo(() => {
		for (let i = messages.length - 1; i >= 0; i--) {
			const isLastMsg = i === messages.length - 1;
			const isStreamingMsg = isLastMsg && isLoading && messages[i].role === 'assistant';
			if (messages[i].role === 'assistant' && messages[i].id && !isStreamingMsg) return i;
		}
		return -1;
	}, [messages, isLoading]);

	const handleVoiceToggle = useCallback(async () => {
		if (isVoiceRecording) {
			await stopVoice();
		} else {
			await startVoice();
		}
	}, [isVoiceRecording, startVoice, stopVoice]);

	const handleComposerSubmit = useCallback(
		(content: string) => {
			if (isVoiceRecording) void stopVoice();
			// The new-segment greeter goes away once the user sends any message.
			dismissGreeter();
			sendFromFreshStartSurface(content);
		},
		[isVoiceRecording, stopVoice, dismissGreeter, sendFromFreshStartSurface]
	);

	const handleCancel = useCallback(() => {
		const restoredText = cancelGeneration();
		if (restoredText) {
			composerRef.current?.setInputText(restoredText);
			requestAnimationFrame(() => composerRef.current?.focusInput());
		}
	}, [cancelGeneration]);

	const handleSelectPrompt = useCallback(
		(prompt: string) => {
			const entry = promptChipEntries.find((e) => e.prompt === prompt);
			track(AnalyticsEvent.Bizy.SuggestionClicked, {
				suggestion_id: entry?.key,
				surface: pageType,
				is_trial: isTrial,
			});

			// Selecting a starter prompt counts as sending a message — dismiss the greeter.
			dismissGreeter();
			sendFromFreshStartSurface(
				prompt,
				entry?.categoryId === 'personalized'
					? {
							prompt_source: 'suggested',
							prompt_key: entry.key,
							prompt_category: entry.categoryId,
						}
					: undefined
			);
			requestAnimationFrame(() => composerRef.current?.focusInput());
		},
		[promptChipEntries, pageType, isTrial, track, dismissGreeter, sendFromFreshStartSurface]
	);

	const conflictKeepRef = useRef<HTMLButtonElement>(null);

	const handlePositiveFeedback = useCallback(
		(messageId: string) => {
			track(AnalyticsEvent.Bizy.FeedbackPositive, { path: window.location.pathname });
			submitFeedback({ messageId, feedback_type: 'positive' }, { onSuccess: () => updateMessageFeedback(messageId, 'positive') });
		},
		[submitFeedback, updateMessageFeedback, track]
	);

	const handleNegativeFeedback = useCallback(
		(messageId: string) => {
			track(AnalyticsEvent.Bizy.FeedbackNegativeStarted, { path: window.location.pathname });
			setFeedbackMessageId(messageId);
			setIsFeedbackModalOpen(true);
		},
		[track]
	);

	const handleClearFeedback = useCallback(
		(messageId: string) => {
			track(AnalyticsEvent.Bizy.FeedbackCleared, { path: window.location.pathname });
			submitFeedback({ messageId, feedback_type: null }, { onSuccess: () => updateMessageFeedback(messageId, undefined) });
		},
		[submitFeedback, updateMessageFeedback, track]
	);

	const handleFeedbackModalSubmit = useCallback(
		(category: string, details?: string) => {
			if (!feedbackMessageId) return;
			track(AnalyticsEvent.Bizy.FeedbackNegativeSubmitted, { category, path: window.location.pathname });
			submitFeedback(
				{
					messageId: feedbackMessageId,
					feedback_type: 'negative',
					feedback_category: category,
					feedback_details: details,
				},
				{
					onSuccess: () => {
						updateMessageFeedback(feedbackMessageId, 'negative');
						setIsFeedbackModalOpen(false);
						setFeedbackMessageId(null);
					},
				}
			);
		},
		[feedbackMessageId, submitFeedback, updateMessageFeedback, track]
	);

	const handleShareToggle = useCallback(
		(id: string, shared: boolean) => {
			shareConversation(
				{ conversationId: id, shared },
				{
					onSuccess: () => {
						toast({
							title: shared ? t('sidePanel.chatSharedSuccess') : t('sidePanel.chatUnsharedSuccess'),
							status: 'success',
							duration: 3000,
						});
					},
					onError: () => {
						toast({ title: t('sidePanel.chatShareError'), status: 'error', duration: 3000 });
					},
				}
			);
		},
		[shareConversation, t, toast]
	);

	const activeConversation = recentConversations.find((c) => c.conversationId === conversationId);
	const activeConversationQueryKey = bizyUserConversationQueryKey(conversationId);
	const { data: cachedActiveConversation } = useQuery<BizyUserConversationDetail | undefined>({
		queryKey: activeConversationQueryKey,
		queryFn: () => Promise.resolve(queryClient.getQueryData<BizyUserConversationDetail>(activeConversationQueryKey)),
		enabled: false,
	});
	const activeSharedWithAdmin = cachedActiveConversation?.sharedWithAdmin ?? activeConversation?.sharedWithAdmin ?? false;

	// The fresh-start greeting cluster (admin intro / What's New tip / mascot +
	// greeting). Built once so the true empty state and the idle blank-slate
	// region (BIZY-683) share the same prop wiring.
	const freshStartGreeting = (
		<BizyFreshStartGreeting
			showAdminIntro={showAdminIntro}
			fontSize={fullscreenFontSize ?? 'sm'}
			showWhatsNewTip={showWhatsNewTip}
			whatsNewTip={whatsNewTip}
			onSelectTipPrompt={(prompt) => {
				track(AnalyticsEvent.Bizy.WhatsNewTipClicked, {
					tipId: whatsNewTip?.id,
					path: window.location.pathname,
				});
				dismissWhatsNewTip();
				sendFromFreshStartSurface(prompt);
				requestAnimationFrame(() => composerRef.current?.focusInput());
			}}
			bizyState={bizyState}
			bizyAnchorRef={emptyChatBizyAnchorRef}
			isBizyHidden={isChatBizyHidden}
			holdSpinAnimation={bizyHoldSpinAnimation}
			holdSpinTransition={bizyHoldSpinTransition}
			showNotificationGreeting={showNotificationGreeting}
			isGamePromptOpen={isBizyGamePromptOpen}
			onBizyClick={handleBizyClick}
			onBizyPointerDown={handleBizyPointerDown}
			onBizyPointerEnd={handleBizyPointerEnd}
			onBizyKeyDown={handleBizyKeyDown}
			onBizyKeyUp={handleBizyKeyUp}
			onGameInvitePlay={handleBizyGameInvitePlay}
			onGameInviteDismiss={handleBizyGameInviteDismiss}
			greetingText={greetingText}
			// Trial onboarding copy is first-launch content — the blank slate
			// falls through to the familiar greeting like everyone else.
			showTrialWelcome={isTrial && pageType === 'home' && !isBlankSlate}
			displayName={displayName}
		/>
	);

	// Prop bundle shared by archived and live timeline messages (per-message
	// props — key, archived, streaming/anchor state — stay at the call sites).
	const timelineMessageSharedProps: Omit<
		BizyTimelineMessageProps,
		'message' | 'archived' | 'isStreamingMessage' | 'isLatestReply' | 'anchorRef' | 'dividerTimestamp'
	> = {
		chatTextFontSize,
		fullscreenFontSize,
		conversationId,
		hideSources: isContinuous,
		toolProgress: toolProgressItems,
		thinkingPhrase: thinkingPhrases[thinkingPhraseIdx],
		followUpsDisabled: inputDisabled,
		rendererProps: bizyKitRendererProps,
		dispatchUiBlockAction: uiBlockActionDispatcher,
		onSendChatMessage: sendChatMessageWithScroll,
		onChoiceUpdate: updateNodeChoice,
		onSuggestedPageClick: handleSuggestedPageClick,
		onPositiveFeedback: handlePositiveFeedback,
		onNegativeFeedback: handleNegativeFeedback,
		onClearFeedback: handleClearFeedback,
	};

	const titleElement = (
		<HStack spacing={isHistoryDrawerOpen ? 0 : 1} minW={0}>
			{/* Compact continuous mode: the "‹ Bizy" control opens the level-1 menu
			    (People list + settings). Fullscreen shows a reopen trigger only while
			    the People sidebar is collapsed (BIZY-665). */}
			{isContinuous && isPeopleBrowserEnabled && !isFullscreen && (
				<Button
					variant="ghost"
					colorScheme="secondary"
					size="sm"
					leftIcon={<CheveronSmallLeftOutline boxSize={5} />}
					color="text.default"
					fontWeight="semibold"
					px={2}
					onClick={onOpenHistory}
					sx={{ '.chakra-button__icon': { marginInlineEnd: 1 } }}
				>
					{t('menu.title')}
				</Button>
			)}
			{isContinuous && isPeopleBrowserEnabled && isFullscreen && isPeopleBrowserCollapsed && (
				<BizyHeaderIconButton label={t('sidePanel.openBizyMenu')} icon={<MenuAlt2Outline boxSize={5} />} onClick={togglePeopleBrowserCollapsed} />
			)}
			{!isContinuous && !isHistoryDrawerOpen && (
				<BizyHeaderIconButton label={t('sidePanel.openBizyMenu')} icon={<MenuAlt2Outline boxSize={5} />} onClick={onOpenHistory} />
			)}
			{!isContinuous && (
				<Text fontSize={isFullscreen ? 'lg' : 'md'} fontWeight="semibold" color="text.default" overflow="hidden" textOverflow="ellipsis" whiteSpace="nowrap">
					{conversationTitle}
				</Text>
			)}
			{!isContinuous && activeSharedWithAdmin && (
				<Badge colorScheme="purple" size="sm" flexShrink={0}>
					{t('sidePanel.sharedBadge')}
				</Badge>
			)}
		</HStack>
	);

	return (
		<Flex
			direction="column"
			h="100%"
			position="relative"
			// Hovering anywhere in the panel acknowledges the attention
			// animation on the in-chat Bizy when a new proactive notification
			// has arrived. Click on the Bizy itself still engages.
			onMouseEnter={isCallingAttentionInChat ? () => setHasAcknowledgedAttention(true) : undefined}
		>
			<BizySidePanelHeader
				left={titleElement}
				pl={isFullscreen && isHistoryDrawerOpen ? 5 : 3}
				right={
					<HStack spacing={0}>
						{showFullscreenToggle && (
							<BizyHeaderIconButton
								label={isFullscreen ? t('sidePanel.exitFullscreen') : t('sidePanel.fullscreen')}
								icon={isFullscreen ? <MinimizeOutline boxSize={4} /> : <MaximizeOutline boxSize={4} />}
								onClick={toggleFullscreen}
							/>
						)}
						{!isContinuous && isBonuslyCompany && conversationId && (
							<BizyHeaderIconButton
								label={activeSharedWithAdmin ? t('sidePanel.hideChatFromAdmins') : t('sidePanel.shareChatWithAdmins')}
								icon={<ShareOutline boxSize={5} />}
								color={activeSharedWithAdmin ? 'primary.fg' : 'secondary.fg'}
								onClick={() => handleShareToggle(conversationId, !activeSharedWithAdmin)}
							/>
						)}
						{isContinuous && <BizyHeaderIconButton label={t('sidePanel.settings')} icon={<CogOutline boxSize={5} />} onClick={() => showSettings()} />}
						<BizyHeaderIconButton label={t('sidePanel.close')} icon={<XOutline boxSize={5} />} onClick={() => closePanel()} />
					</HStack>
				}
			/>

			<Flex direction="column" flex={1} minH={0}>
				<Box position="relative" flex={1} overflow="hidden">
					<Box
						position="absolute"
						top={0}
						left={0}
						right={0}
						height="24px"
						background="linear-gradient(to bottom, var(--chakra-colors-white) 0%, transparent 100%)"
						_dark={{ background: 'linear-gradient(to bottom, var(--chakra-colors-gray-900) 0%, transparent 100%)' }}
						pointerEvents="none"
						opacity={isScrolledFromTop ? 1 : 0}
						transition="opacity 0.15s ease-in-out"
						zIndex={1}
					/>
					<Box
						ref={(node) => {
							(scroll.containerRef as React.MutableRefObject<HTMLDivElement | null>).current = node;
							(scrollAnchorRef as React.MutableRefObject<HTMLDivElement | null>).current = node;
						}}
						data-bizy-chat-scroll
						height="100%"
						overflowY="auto"
						overscrollBehavior="contain"
						onScroll={(e: React.UIEvent<HTMLDivElement>) => {
							const { scrollTop } = e.currentTarget;
							setIsScrolledFromTop(scrollTop > 10);
							// Skip pagination during programmatic scrolls (send-scroll /
							// blank-slate animation): a prepend mid-animation would shift
							// the scroll target.
							if (isContinuous && hasMoreHistory && !isLoadingOlder && !scroll.isSmoothScrolling() && scrollTop < 200) {
								void loadOlderTurns(beforePrepend);
							}
						}}
						sx={{ scrollPaddingTop: '36px' }}
						{...(isEmptyState ? { display: 'flex', flexDirection: 'column' as const } : {})}
					>
						<Flex
							direction="column"
							gap={2}
							px={4}
							pt={3}
							flex={isEmptyState ? 1 : undefined}
							minH={0}
							maxW={isFullscreen ? '744px' : undefined}
							mx={isFullscreen ? 'auto' : undefined}
							w="100%"
						>
							{isLoadingConversation && (
								<Flex direction="column" align="center" justify="center" flex={1} py={8} gap={3}>
									<Spinner size="lg" color="primary.fg" />
									<Text fontSize={fullscreenFontSize ?? 'sm'} color="text.soft">
										{t('sidePanel.loadingConversation')}
									</Text>
								</Flex>
							)}
							{!isLoadingConversation && isEmptyState && isPanelOpen && panelView === 'chat' && isPromptSuggestionsReady && !showGreeter && freshStartGreeting}
							{!isLoadingConversation &&
								archivedSegments.map((archivedSegment, segmentIndex) => (
									<Fragment key={`fresh-start-segment-${segmentIndex}`}>
										{archivedSegment.messages.map((message, index) => (
											<BizyTimelineMessage
												key={message.key || message.id || index}
												message={message}
												archived
												isStreamingMessage={false}
												isLatestReply={false}
												dividerTimestamp={archivedSegmentDividers[segmentIndex]?.get(index)}
												{...timelineMessageSharedProps}
											/>
										))}
										<BizyTimeDivider timestamp={archivedSegment.boundaryAt} variant="fresh-start" />
									</Fragment>
								))}
							{!isLoadingConversation &&
								messages.map((message, index) => (
									<BizyTimelineMessage
										key={message.key || message.id || index}
										message={message}
										archived={false}
										isStreamingMessage={index === messages.length - 1 && isStreaming}
										isLatestReply={index === lastCompleteAssistantIndex}
										anchorRef={index === lastUserMsgIndex ? lastUserMsgRef : undefined}
										dividerTimestamp={messageDividers.get(index)}
										{...timelineMessageSharedProps}
									/>
								))}

							{showThinkingIndicator && (
								<Box minH="20px" overflow="hidden" pl={1}>
									<AnimatePresence mode="wait" initial={false}>
										{toolProgressItems.length > 0 ? (
											toolProgressItems.map((item, i) => {
												const isLatest = i === toolProgressItems.length - 1;
												return (
													<motion.div
														key={`${i}-${item}`}
														initial={{ opacity: 0, y: 4 }}
														animate={{ opacity: 1, y: 0 }}
														exit={{ opacity: 0, y: -4 }}
														transition={{ duration: 0.35 }}
													>
														<Text
															fontSize="xs"
															fontWeight="medium"
															color="text.softest"
															sx={
																isLatest
																	? {
																			'@keyframes pulse': {
																				'0%, 100%': { opacity: 1 },
																				'50%': { opacity: 0.4 },
																			},
																			animation: 'pulse 1.5s ease-in-out infinite',
																		}
																	: { opacity: 0.6 }
															}
														>
															{item}
														</Text>
													</motion.div>
												);
											})
										) : (
											<motion.div
												key={thinkingPhraseIdx}
												initial={{ opacity: 0, y: 4 }}
												animate={{ opacity: 1, y: 0 }}
												exit={{ opacity: 0, y: -4 }}
												transition={{ duration: 0.35 }}
											>
												<Text
													fontSize="xs"
													fontWeight="medium"
													color="text.softest"
													sx={{
														'@keyframes pulse': {
															'0%, 100%': { opacity: 1 },
															'50%': { opacity: 0.4 },
														},
														animation: 'pulse 1.5s ease-in-out infinite',
													}}
												>
													{thinkingPhrases[thinkingPhraseIdx]}
												</Text>
											</motion.div>
										)}
									</AnimatePresence>
								</Box>
							)}

							{isTimedOut && (
								<Flex alignSelf="flex-start" direction="column" gap={1} pl={1}>
									<Text fontSize={fullscreenFontSize ?? 'sm'} color="text.softer">
										{t('timeout.message')}
									</Text>
									<Button variant="link" size="sm" color="text.interactive" onClick={retryLastMessage} px={0} justifyContent="flex-start">
										{t('timeout.retry')}
									</Button>
								</Flex>
							)}

							{showGreeter && !isLoadingConversation && (
								<BizyGreeterCallout
									unreadEntries={unreadEntries}
									timeOfDayKey={greeterTimeOfDayKey}
									fontSize={fullscreenFontSize}
									momentCount={greeterEntries.length}
									connectedCount={connectedConnectorCount}
								/>
							)}

							{messages.length > 0 && isPanelOpen && !isLoadingConversation && !showGreeter && !isBlankSlate && (
								<motion.div
									ref={activeChatBizyAnchorRef}
									layout
									transition={{ type: 'spring', stiffness: 200, damping: 25 }}
									style={{
										display: 'flex',
										flexDirection: 'column',
										alignItems: 'center',
										padding: '8px 0',
										visibility: isChatBizyHidden ? 'hidden' : 'visible',
										position: 'relative',
									}}
									// Bind click + a11y while there's an unread to engage with —
									// not while wiggling. The outer panel's onMouseEnter flips
									// hasAcknowledgedAttention true as soon as the cursor crosses
									// the panel boundary, so gating the click on
									// isCallingAttentionInChat would un-bind the handler before
									// the click on the Bizy could land. The wiggle animation
									// still depends on the ack flag so hover visually settles it.
								>
									<motion.div
										animate={isCallingAttentionInChat ? { x: [-12, 12, -12], y: [0, -6, 0], rotate: [-8, 8, -8] } : bizyHoldSpinAnimation}
										transition={isCallingAttentionInChat ? { duration: 0.7, ease: 'easeInOut', repeat: Infinity, repeatType: 'loop' } : bizyHoldSpinTransition}
										style={{ display: 'flex', cursor: 'pointer' }}
										onClick={handleBizyClick}
										onPointerDown={handleBizyPointerDown}
										onPointerUp={handleBizyPointerEnd}
										onPointerCancel={handleBizyPointerEnd}
										onKeyDown={handleBizyKeyDown}
										onKeyUp={handleBizyKeyUp}
										role="button"
										tabIndex={0}
										aria-label={showNotificationGreeting ? t('sidePanel.openNewBizyMessage') : t('gameInvite.ariaLabel')}
									>
										<Bizy state={bizyState} size="md" animations={isCallingAttentionInChat ? { wingSpeed: 0.12, wingAmplitude: 0.5 } : undefined} />
									</motion.div>
									<AnimatePresence>
										{isBizyGamePromptOpen && !showNotificationGreeting && (
											<motion.div
												key="active-bizy-game-invite"
												initial={{ opacity: 0, x: '-50%', y: 6, scale: 0.96 }}
												animate={{ opacity: 1, x: '-50%', y: 0, scale: 1 }}
												exit={{ opacity: 0, x: '-50%', y: 4, scale: 0.96 }}
												transition={{ duration: 0.18 }}
												style={{ position: 'absolute', left: '50%', bottom: 'calc(100% + 10px)', zIndex: 2, pointerEvents: 'auto' }}
											>
												<BizyGameInvite onPlay={handleBizyGameInvitePlay} onDismiss={handleBizyGameInviteDismiss} />
											</motion.div>
										)}
									</AnimatePresence>
								</motion.div>
							)}

							{/* Idle blank slate (BIZY-683): time divider + a fresh-start
							    viewport below the old conversation. The region's min-height
							    gives the scroll room so the divider can sit near the top. */}
							{blankSlate && !isLoadingConversation && !showGreeter && (
								<>
									<BizyTimeDivider timestamp={blankSlate.boundaryAt} variant="fresh-start" dividerRef={blankSlateDividerRef} />
									<Flex direction="column" minH={`${blankSlate.minHeight}px`} data-component="bizy-blank-slate">
										{freshStartGreeting}
									</Flex>
								</>
							)}
						</Flex>
					</Box>
				</Box>

				<Box
					px={isFullscreen ? 2 : 3}
					pb={2}
					maxW={isFullscreen ? '744px' : undefined}
					mx={isFullscreen ? 'auto' : undefined}
					w="100%"
					sx={{ paddingBottom: 'max(var(--chakra-space-3), env(safe-area-inset-bottom))' }}
				>
					{showGreeter && isPromptSuggestionsReady ? (
						// Greeter is active: show starter prompts near the composer regardless
						// of empty state, under an "Or ask me anything" divider (BIZY-638).
						<>
							<Text fontSize="xs" color="text.softest" textAlign="center" pb={2}>
								{t('greeter.orAskAnything')}
							</Text>
							<BizySidePanelSuggestedPrompts fontSize={fullscreenFontSize} items={promptChips} onSelectPrompt={handleSelectPrompt} layout="vertical" />
						</>
					) : (
						<>
							{showFreshStart && !showWhatsNewTip && isPromptSuggestionsReady && (
								<BizySidePanelSuggestedPrompts fontSize={fullscreenFontSize} items={promptChips} onSelectPrompt={handleSelectPrompt} layout="vertical" />
							)}
							{!showFreshStart && showPagePrompts && !isLoading && (
								<BizySidePanelSuggestedPrompts
									fontSize={fullscreenFontSize}
									items={pageOnlyPromptChips}
									onSelectPrompt={handleSelectPrompt}
									layout="horizontal"
								/>
							)}
						</>
					)}
					{isOtherTabLoading && (
						<Text fontSize="xs" color="text.softest" px={1} pb={1}>
							{t('otherTab.responding')}
						</Text>
					)}
					<Box position="relative">
						{isPanelOpen && <BizyAmbientGlow />}
						{isBizyAdminOnly && (
							<BizyInputBanner
								message={t('inputBanner.adminOnly.message')}
								action={
									<Link as={RouterLink} to="/admin/company/settings/ai" fontSize="sm" fontWeight="medium" color="text.interactive">
										{t('inputBanner.adminOnly.learnMore')}
									</Link>
								}
							/>
						)}
						<BizyComposer
							ref={composerRef}
							onSubmit={handleComposerSubmit}
							onCancel={handleCancel}
							isLoading={isLoading}
							disabled={isOtherTabLoading}
							placeholder={
								pendingContextualInsert
									? t(`contextualInvocation.inputPlaceholder.${pendingContextualInsert.surface}`)
									: pendingFeatureRequest?.promptHint || t('inputPlaceholder')
							}
							fontSize={chatTextFontSize}
							onVoiceToggle={handleVoiceToggle}
							isVoiceRecording={isVoiceRecording}
							isVoiceStarting={isVoiceStarting}
							onDraftStateChange={setIsComposerDrafting}
							onInputChange={(value) => {
								// Typing is direct engagement — mark any unread proactive notification
								// as seen without yanking the user out of their current conversation.
								acknowledgeUnread();
								// Dismiss the What's New tip when the user starts typing.
								if (showWhatsNewTip && value.trim()) {
									track(AnalyticsEvent.Bizy.WhatsNewTipDismissed, {
										tipId: whatsNewTip?.id,
										reason: 'typing',
										path: window.location.pathname,
									});
									dismissWhatsNewTip();
								}
							}}
							// Use onClick instead of onFocus — the panel auto-focuses the input
							// on open, and programmatic focus shouldn't consume an unread that
							// the user hasn't actually acknowledged yet.
							onClick={() => acknowledgeUnread()}
							footerLeft={promptLibraryFooterContent}
						/>
					</Box>
				</Box>
			</Flex>

			{promptLibraryModal}

			{isBizyGameOpen && !showNotificationGreeting && (
				<Suspense fallback={null}>
					<LazyBizySwarmGame onClose={closeBizyGame} />
				</Suspense>
			)}

			<BizyFeedbackModal
				isOpen={isFeedbackModalOpen}
				onClose={() => {
					track(AnalyticsEvent.Bizy.FeedbackNegativeCancelled, { path: window.location.pathname });
					setIsFeedbackModalOpen(false);
					setFeedbackMessageId(null);
				}}
				onSubmit={handleFeedbackModalSubmit}
			/>

			<AlertDialog
				leastDestructiveRef={conflictKeepRef as RefObject<HTMLButtonElement>}
				isOpen={showExternalOpenConflict}
				onClose={handleKeepCurrentConversation}
				isCentered
			>
				<AlertDialogOverlay>
					<AlertDialogContent>
						<AlertDialogHeader fontSize="lg" fontWeight="bold">
							{t('sidePanel.newConversationTitle')}
						</AlertDialogHeader>
						<AlertDialogCloseButton />
						<AlertDialogBody>
							<Text>{t('sidePanel.newConversationConfirm')}</Text>
						</AlertDialogBody>
						<AlertDialogFooter gap={3}>
							<Button ref={conflictKeepRef} variant="outline" colorScheme="secondary" onClick={handleKeepCurrentConversation}>
								{t('sidePanel.keepCurrent')}
							</Button>
							<Button variant="solid" colorScheme="primary" onClick={handleConfirmNewConversation}>
								{t('sidePanel.startNew')}
							</Button>
						</AlertDialogFooter>
					</AlertDialogContent>
				</AlertDialogOverlay>
			</AlertDialog>
		</Flex>
	);
}
